import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ClipboardCheck, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../../../lib/utils';
import { programOrgDisplay } from '../../_shared/components/GovernmentOrgPicker';
import { programRoleAssignmentHooks } from '../relationshipHooks';
import { formatCurrency, formatProgramDate, pricingLabel } from './programHomeUi';
/** Uppercase section header with an optional right-aligned action link. */ function SectionHeader({ children, action }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "mb-2.5 flex items-baseline justify-between gap-2",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                children: children
            }),
            action
        ]
    });
}
/**
 * State/role row: darker label ⟷ light value/link, hairline-separated
 * (mockup Operational State + Roles blocks — 7px padding, faint bottom rules).
 */ function StateRow({ label, value, muted }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center justify-between gap-2.5 border-b border-border/50 py-[7px] last:border-0",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "shrink-0 text-[12.5px] text-foreground/70",
                children: label
            }),
            /*#__PURE__*/ _jsx("span", {
                className: cn('min-w-0 truncate text-right text-[12px]', muted ? 'text-muted-foreground' : 'font-medium text-foreground'),
                children: value
            })
        ]
    });
}
/**
 * Contract fact row: muted label ⟷ dark value, no separators, tighter 6px
 * padding (mockup <dl> block). `strong` bolds key figures (PIID, TCV); `muted`
 * softens unset values to a faint em-dash / "Not set".
 */ function FactRow({ label, value, strong, muted }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center justify-between gap-3 py-[6px]",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "shrink-0 text-[12.5px] text-muted-foreground",
                children: label
            }),
            /*#__PURE__*/ _jsx("span", {
                className: cn('min-w-0 truncate text-right text-[12.5px] tabular-nums text-foreground', strong && 'font-medium', muted && 'font-normal text-muted-foreground'),
                children: value
            })
        ]
    });
}
/**
 * Program briefing rail (LAYOUT-SPEC §2A / mockup 228-309): reference facts as
 * labelled rows — Operational State, Contract, Roles, Related. Read-first; all
 * editing routes through the "All fields" dialog (onOpenFullEdit), matching the
 * mockup (no inline pills, no Delete button — Delete lives in that dialog).
 */ export function ProgramBriefingPanel({ program, programId, canEdit, onOpenFullEdit }) {
    const display = programOrgDisplay(program);
    const roles = programRoleAssignmentHooks.useList({
        programId
    });
    const assignments = roles.data?.items ?? [];
    const award = formatProgramDate(program.awardDate);
    const effective = formatProgramDate(program.effectiveDate);
    const tcv = formatCurrency(program.tcvOnAward);
    const rolesHref = `/delivery/programs/${programId}/roles`;
    // Empty operational values render as a "Set" affordance (opens All fields)
    // for editors, or a muted em dash for read-only viewers.
    const stateValue = (label)=>{
        if (label) return label;
        if (!canEdit) return '—';
        return /*#__PURE__*/ _jsx("button", {
            className: "text-[12px] text-muted-foreground hover:text-foreground",
            onClick: onOpenFullEdit,
            type: "button",
            children: "Set"
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[26px]",
        children: [
            /*#__PURE__*/ _jsxs("section", {
                children: [
                    /*#__PURE__*/ _jsx(SectionHeader, {
                        children: "Operational State"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(StateRow, {
                                label: "Operational",
                                value: stateValue(program.operationalStateLabel),
                                muted: !program.operationalStateLabel
                            }),
                            /*#__PURE__*/ _jsx(StateRow, {
                                label: "Lifecycle",
                                value: stateValue(program.contractLifecycleStateLabel),
                                muted: !program.contractLifecycleStateLabel
                            }),
                            /*#__PURE__*/ _jsx(StateRow, {
                                label: "Record",
                                value: stateValue(program.recordStatusLabel),
                                muted: !program.recordStatusLabel
                            }),
                            /*#__PURE__*/ _jsx(StateRow, {
                                label: "Delivery model",
                                value: stateValue(program.deliveryModelTypeLabel),
                                muted: !program.deliveryModelTypeLabel
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                children: [
                    /*#__PURE__*/ _jsx(SectionHeader, {
                        action: /*#__PURE__*/ _jsx("button", {
                            className: "text-[12px] text-muted-foreground hover:text-foreground",
                            onClick: onOpenFullEdit,
                            type: "button",
                            children: canEdit ? 'All fields' : 'View'
                        }),
                        children: "Contract"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col",
                        children: [
                            /*#__PURE__*/ _jsx(FactRow, {
                                label: "PIID",
                                value: program.contractNumber || '—',
                                strong: !!program.contractNumber,
                                muted: !program.contractNumber
                            }),
                            award ? /*#__PURE__*/ _jsx(FactRow, {
                                label: "Awarded",
                                value: award
                            }) : null,
                            effective ? /*#__PURE__*/ _jsx(FactRow, {
                                label: "Effective",
                                value: effective
                            }) : null,
                            tcv ? /*#__PURE__*/ _jsx(FactRow, {
                                label: "TCV",
                                value: tcv,
                                strong: true
                            }) : null,
                            /*#__PURE__*/ _jsx(FactRow, {
                                label: "Pricing",
                                value: pricingLabel(program.pricingType) || '—'
                            }),
                            /*#__PURE__*/ _jsx(FactRow, {
                                label: "Role",
                                value: display.companyRole,
                                muted: display.companyRole === '—'
                            }),
                            /*#__PURE__*/ _jsx(FactRow, {
                                label: "Prime",
                                value: display.prime,
                                muted: display.prime === '—'
                            }),
                            /*#__PURE__*/ _jsx(FactRow, {
                                label: "Contracting office",
                                value: display.contracting,
                                muted: display.contracting === '—'
                            }),
                            /*#__PURE__*/ _jsx(FactRow, {
                                label: "Customer",
                                value: display.customer,
                                muted: display.customer === '—'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 text-[11px] leading-normal text-muted-foreground",
                        children: "Maintained by the DM Team to preserve data integrity."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                children: [
                    /*#__PURE__*/ _jsx(SectionHeader, {
                        action: /*#__PURE__*/ _jsx(Link, {
                            className: "text-[12px] text-muted-foreground hover:text-foreground",
                            to: rolesHref,
                            children: "Manage →"
                        }),
                        children: "Roles"
                    }),
                    assignments.length > 0 ? /*#__PURE__*/ _jsx("div", {
                        children: assignments.map((a)=>/*#__PURE__*/ _jsx(StateRow, {
                                label: a.roleTypeLabel || 'Role',
                                value: a.contactDisplayName || 'Not assigned',
                                muted: !a.contactDisplayName
                            }, a.id))
                    }) : /*#__PURE__*/ _jsxs("p", {
                        className: "text-[12.5px] text-muted-foreground",
                        children: [
                            "No roles assigned.",
                            ' ',
                            /*#__PURE__*/ _jsx(Link, {
                                className: "text-info hover:underline",
                                to: rolesHref,
                                children: "Assign →"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                children: [
                    /*#__PURE__*/ _jsx(SectionHeader, {
                        children: "Related"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                className: "inline-flex items-center gap-[7px] text-[12.5px] text-foreground hover:underline",
                                to: `/delivery/programs/${programId}/docs`,
                                children: [
                                    /*#__PURE__*/ _jsx(FileText, {
                                        className: "h-[13px] w-[13px] text-muted-foreground"
                                    }),
                                    "Program documents"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Link, {
                                className: "inline-flex items-center gap-[7px] text-[12.5px] text-foreground hover:underline",
                                to: `/delivery/programs/${programId}/audits`,
                                children: [
                                    /*#__PURE__*/ _jsx(ClipboardCheck, {
                                        className: "h-[13px] w-[13px] text-muted-foreground"
                                    }),
                                    "Audits & compliance"
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
